/**
 * DashboardWidgetManager
 *
 * Full-modal widget visibility editor for the Dashboard page.
 * Manages the server-side layout (user_dashboard_widgets) and syncs
 * back to the in-memory state used by the Dashboard page.
 *
 * Props:
 *   widgets      — full UNIVERSAL_WIDGETS array from App.tsx
 *   currentState — snapshot of current useWidgetVisibility state
 *   onSave       — callback to update in-memory state after save
 *   userKey      — logged-in user's user_key (for DB writes)
 *   authFetch    — authenticated fetch from useAuthFetch()
 */
import React, { useState, useCallback, useMemo } from 'react';
import { useAuth } from '../AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DashboardWidget {
  id: string;
  label: string;
  category: 'kpi' | 'chart' | 'table';
  defaultVisible: boolean;
  defaultOrder: number;
  source: string;
  description?: string;
}

interface WidgetState {
  visible: boolean;
  order: number;
}

export interface DashboardWidgetManagerProps {
  widgets: DashboardWidget[];
  currentState: Record<string, WidgetState>;
  onSave: (newState: Record<string, WidgetState>) => void;
  userKey: number;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SOURCE_META: Record<string, { label: string; color: string; textColor: string; borderColor: string }> = {
  dashboard:    { label: 'Dashboard',    color: 'bg-blue-50',   textColor: 'text-blue-700',   borderColor: 'border-blue-200'   },
  operations:   { label: 'Operations',   color: 'bg-green-50',  textColor: 'text-green-700',  borderColor: 'border-green-200'  },
  performance:  { label: 'Performance',  color: 'bg-purple-50', textColor: 'text-purple-700', borderColor: 'border-purple-200' },
  patterns:     { label: 'Patterns',     color: 'bg-orange-50', textColor: 'text-orange-700', borderColor: 'border-orange-200' },
  shareholders: { label: 'Shareholders', color: 'bg-indigo-50', textColor: 'text-indigo-700', borderColor: 'border-indigo-200' },
};

const CATEGORY_LABEL: Record<string, string> = {
  kpi:   'KPI',
  chart: 'Chart',
  table: 'Table',
};

const CATEGORY_BADGE: Record<string, string> = {
  kpi:   'bg-yellow-100 text-yellow-700',
  chart: 'bg-blue-100 text-blue-700',
  table: 'bg-gray-100 text-gray-600',
};

const SOURCE_ORDER = ['dashboard', 'operations', 'performance', 'patterns', 'shareholders'];

// ─── Component ────────────────────────────────────────────────────────────────

const DashboardWidgetManager: React.FC<DashboardWidgetManagerProps> = ({
  widgets,
  currentState,
  onSave,
  userKey,
  authFetch,
}) => {
  const { user } = useAuth();

  // Only show widgets for pages the user has view access to
  const accessibleWidgets = widgets.filter(w =>
    user?.permissions?.[w.source]?.view === true
  );

  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [expandedSource, setExpandedSource] = useState<string>('dashboard');

  // Local editing copy — initialised from currentState when modal opens
  const [editState, setEditState] = useState<Record<string, WidgetState>>({});

  const openModal = useCallback(() => {
    // Take a snapshot of currentState for editing
    const snapshot = accessibleWidgets.reduce((acc, w) => ({
      ...acc,
      [w.id]: {
        visible: currentState[w.id]?.visible ?? w.defaultVisible,
        order:   currentState[w.id]?.order   ?? w.defaultOrder,
      },
    }), {} as Record<string, WidgetState>);
    setEditState(snapshot);
    setSaveError(null);
    setSaveSuccess(false);
    setIsOpen(true);
  }, [accessibleWidgets, currentState]);

  const closeModal = () => setIsOpen(false);

  // ── Widget list per source, sorted by current order ──────────────────────
  const widgetsBySource = useMemo(() => {
    const bySource: Record<string, (DashboardWidget & WidgetState)[]> = {};
    for (const source of SOURCE_ORDER) {
      bySource[source] = accessibleWidgets
        .filter(w => w.source === source)
        .map(w => ({
          ...w,
          visible: editState[w.id]?.visible ?? w.defaultVisible,
          order:   editState[w.id]?.order   ?? w.defaultOrder,
        }))
        .sort((a, b) => a.order - b.order);
    }
    return bySource;
  }, [accessibleWidgets, editState]);

  // ── Editing helpers ───────────────────────────────────────────────────────

  const toggleWidget = (id: string) => {
    setEditState(prev => ({
      ...prev,
      [id]: { ...prev[id], visible: !prev[id].visible },
    }));
  };

  const moveWidget = (id: string, source: string, direction: 'up' | 'down') => {
    setEditState(prev => {
      const list = accessibleWidgets
        .filter(w => w.source === source)
        .map(w => ({ id: w.id, order: prev[w.id]?.order ?? w.defaultOrder }))
        .sort((a, b) => a.order - b.order);

      const idx = list.findIndex(w => w.id === id);
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= list.length) return prev;

      const swapId = list[swapIdx].id;
      return {
        ...prev,
        [id]:     { ...prev[id],     order: prev[swapId].order   ?? list[swapIdx].order },
        [swapId]: { ...prev[swapId], order: prev[id].order       ?? list[idx].order     },
      };
    });
  };

  const toggleSourceAll = (source: string, visible: boolean) => {
    setEditState(prev => {
      const next = { ...prev };
      accessibleWidgets.filter(w => w.source === source).forEach(w => {
        next[w.id] = { ...next[w.id], visible };
      });
      return next;
    });
  };

  const resetToDefaults = () => {
    setEditState(
      accessibleWidgets.reduce((acc, w) => ({
        ...acc,
        [w.id]: { visible: w.defaultVisible, order: w.defaultOrder },
      }), {} as Record<string, WidgetState>)
    );
  };

  // ── Save ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);

    const layout = accessibleWidgets.map(w => ({
      widget_key: w.id,
      is_visible: editState[w.id]?.visible ?? w.defaultVisible,
      sort_order: editState[w.id]?.order   ?? w.defaultOrder,
    }));

    try {
      const res = await authFetch('/api/users/widget-permissions?scope=dashboard', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_key: userKey, layout }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error || `Server error ${res.status}`);
      }

      // Commit to in-memory state and localStorage
      onSave(editState);

      // Notify any other listeners (e.g. analytics pages)
      window.dispatchEvent(new CustomEvent('dashboard-layout-changed'));

      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
        closeModal();
      }, 1000);
    } catch (err: any) {
      setSaveError(err.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Stats ─────────────────────────────────────────────────────────────────

  const totalVisible = useMemo(
    () => accessibleWidgets.filter(w => editState[w.id]?.visible ?? w.defaultVisible).length,
    [accessibleWidgets, editState]
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={openModal}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors shadow-sm"
        title="Manage dashboard layout"
      >
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
        <span className="text-gray-700">Layout</span>
        <span className="bg-blue-100 text-blue-700 text-xs font-medium px-1.5 py-0.5 rounded">
          {Object.values(currentState).filter(s => s?.visible).length}/{accessibleWidgets.length}
        </span>
      </button>

      {/* Modal overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeModal}
          />

          {/* Panel */}
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col mx-4">

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h2 className="text-base font-semibold text-gray-800">Dashboard Layout</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {totalVisible} of {accessibleWidgets.length} widgets visible · changes are saved to your account
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Global actions */}
            <div className="flex gap-2 px-5 py-2 border-b bg-gray-50">
              <button
                onClick={() => setEditState(accessibleWidgets.reduce((a, w) => ({ ...a, [w.id]: { ...editState[w.id], visible: true } }), { ...editState }))}
                className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
              >
                Show All
              </button>
              <button
                onClick={() => setEditState(accessibleWidgets.reduce((a, w) => ({ ...a, [w.id]: { ...editState[w.id], visible: false } }), { ...editState }))}
                className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                Hide All
              </button>
              <button
                onClick={resetToDefaults}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
              >
                Reset to Defaults
              </button>
            </div>

            {/* Body — scrollable */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {SOURCE_ORDER.map(source => {
                const meta    = SOURCE_META[source];
                const list    = widgetsBySource[source] ?? [];
                if (list.length === 0) return null;
                const visInSrc = list.filter(w => w.visible).length;
                const isExp    = expandedSource === source;

                return (
                  <div key={source} className={`rounded-lg border ${meta.borderColor} overflow-hidden`}>
                    {/* Source header */}
                    <button
                      className={`w-full flex items-center justify-between px-4 py-2.5 ${meta.color} hover:brightness-95 transition-all`}
                      onClick={() => setExpandedSource(isExp ? '' : source)}
                    >
                      <div className="flex items-center gap-2">
                        <svg
                          className={`w-4 h-4 ${meta.textColor} transition-transform ${isExp ? 'rotate-90' : ''}`}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className={`text-sm font-semibold ${meta.textColor}`}>{meta.label}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500">{visInSrc}/{list.length} visible</span>
                        {/* Per-source bulk toggles — stop propagation so they don't collapse */}
                        <button
                          onClick={e => { e.stopPropagation(); toggleSourceAll(source, true); }}
                          className="text-xs text-green-600 hover:text-green-800 underline"
                        >on</button>
                        <button
                          onClick={e => { e.stopPropagation(); toggleSourceAll(source, false); }}
                          className="text-xs text-red-500 hover:text-red-700 underline"
                        >off</button>
                      </div>
                    </button>

                    {/* Widget rows */}
                    {isExp && (
                      <div className="divide-y divide-gray-100">
                        {list.map((widget, idx) => (
                          <div
                            key={widget.id}
                            className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${
                              widget.visible ? 'bg-white' : 'bg-gray-50 opacity-60'
                            }`}
                          >
                            {/* Visibility toggle */}
                            <input
                              type="checkbox"
                              checked={widget.visible}
                              onChange={() => toggleWidget(widget.id)}
                              className="w-4 h-4 rounded accent-blue-600 cursor-pointer flex-shrink-0"
                            />

                            {/* Label + description */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-700 truncate">{widget.label}</p>
                              {widget.description && (
                                <p className="text-xs text-gray-400 truncate">{widget.description}</p>
                              )}
                            </div>

                            {/* Category badge */}
                            <span className={`text-xs px-2 py-0.5 rounded font-medium flex-shrink-0 ${CATEGORY_BADGE[widget.category]}`}>
                              {CATEGORY_LABEL[widget.category]}
                            </span>

                            {/* Order controls */}
                            <div className="flex gap-0.5 flex-shrink-0">
                              <button
                                onClick={() => moveWidget(widget.id, source, 'up')}
                                disabled={idx === 0}
                                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-20 rounded"
                                title="Move up"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                              </button>
                              <button
                                onClick={() => moveWidget(widget.id, source, 'down')}
                                disabled={idx === list.length - 1}
                                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-20 rounded"
                                title="Move down"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-5 py-4 border-t bg-gray-50">
              {/* Error / success feedback */}
              <div className="flex-1 mr-4">
                {saveError && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                    {saveError}
                  </p>
                )}
                {saveSuccess && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Layout saved
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={closeModal}
                  disabled={saving}
                  className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && (
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                  {saving ? 'Saving…' : 'Save Layout'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardWidgetManager;
